import { createElement } from 'react';
import { some, pick, isEmpty, uniqueId, assign } from 'lodash/fp';
import { TYPES } from 'constants/config';
import { setDefaultValue } from 'utils';

const createObject = () => ({});

const isRejectedStep = ({ type }) =>
  [TYPES.WAITSTEP, TYPES.PLACEHOLDER].includes(type);

const getPathIndexes = contents =>
  contents.reduce((acc, current, index) => {
    if (current.type === TYPES.PATH) acc.push(index);
    return acc;
  }, []);

const PARENT_PROPS = ['id', 'title', 'urls'];

export default class PathRenderer {
  constructor(options) {
    this.pathData = options.pathData;
    this.pathComponent = options.pathComponent;
    this.groupComponent = options.groupComponent;
    this.stepPosition = options.startingPosition || 1;
    this.getEmptyPathProps = options.getEmptyPathProps || createObject;
    this.renderedFirstLevelPath = false;
    this.descriptionVisibilityCache = [];
    this.renderPathContents = this.renderPathContents.bind(this);
    this.renderHeaders = setDefaultValue(options.renderHeaders, true);
    this.autoRender = setDefaultValue(this.autoRender, true);
    if (this.autoRender) {
      return this.render();
    }
  }

  getDescriptionVisibility(id) {
    if (!this.descriptionVisibilityCache.includes(id)) {
      this.descriptionVisibilityCache.push(id);
      return true;
    }
    return false;
  }

  getIncrementalStepPosition() {
    return this.stepPosition++; // eslint-disable-line
  }

  isRenderingFirstLevelPath(id, persist = true) {
    const result = this.pathData.id === id;
    if (persist && result) {
      this.renderedFirstLevelPath = true;
    }
    return result;
  }

  shouldRenderAsSubPath(id) {
    if (!this.renderedFirstLevelPath && this.isRenderingFirstLevelPath(id)) {
      return false;
    }
    return true;
  }

  processSteps(steps) {
    if (!Array.isArray(steps)) return steps;
    return steps.reduce((acc, step) => {
      if (isRejectedStep(step)) return acc;
      step.stepNumber = this.getIncrementalStepPosition();
      return acc.concat(step);
    }, []);
  }

  renderPath({ id, ...props }, parent) {
    const isRenderingSubPath = this.shouldRenderAsSubPath(id);
    return createElement(this.pathComponent, {
      id,
      ...props,
      key: parent ? uniqueId(`${id}.of.${parent.id}$`) : uniqueId(id + '$'),
      // nesting
      parent: pick(PARENT_PROPS, parent),
      subPath: isRenderingSubPath,
      // overrides
      options: isRenderingSubPath ? null : props.options,
      contents: this.processSteps(props.contents),
      description: this.getDescriptionVisibility(id) ? props.description : null,
    });
  }

  renderPathContents(path = this.pathData) {
    // caching a steps group starting index
    let stepsStartingIndex;
    // Getting an array of possible nested paths under this level in advance
    const pathIndexes = getPathIndexes(path.contents);
    // iterating through each step of the this level
    return path.contents.reduce((children, current, index) => {
      // if current content is a sub path
      if (pathIndexes[0] === index) {
        pathIndexes.shift(); // we know we about to process a sub-path
        // if the first step of this level happens to be a path, render it as
        // a separate header
        if (this.renderHeaders && index === 0) {
          children.push(
            this.renderPath({ ...path, contents: [], asHeader: true }),
          );
        }
        // perform recursion if this path-step happens to have a sub-path
        const hasNestedPaths = some({ type: TYPES.PATH })(current.contents);
        if (hasNestedPaths) {
          children.push(this.renderPathContents(current));
        } else {
          // if this path-step is empty, merge with getEmptyPathProps
          if (isEmpty(current.contents)) {
            current = assign(current, this.getEmptyPathProps(current));
          }
          // render path-steps group
          children.push(this.renderPath(current, path));
        }
      } else {
        // if current step is a not a path (might skip iteration)
        // keep track of non-path steps for grouping
        stepsStartingIndex = setDefaultValue(stepsStartingIndex, index);
        const nextIndex = index + 1;
        // if there are no more paths OR followed by path
        if (isEmpty(pathIndexes) || nextIndex === pathIndexes[0]) {
          // escape if followed by another step
          if (isEmpty(pathIndexes) && path.contents[nextIndex]) {
            return children;
          }
          // group steps from stepsStartingIndex
          const steps = path.contents.slice(stepsStartingIndex, nextIndex);
          stepsStartingIndex = undefined;
          // set the root path as a parent to the current steps group
          let parent;
          if (!this.isRenderingFirstLevelPath(path.id, /* persist */ false)) {
            parent = this.pathData;
          }
          // render sliced path-steps group
          children.push(this.renderPath({ ...path, contents: steps }, parent));
        }
      }
      return children;
    }, []);
  }

  renderPathWithoutContents(path = this.pathData) {
    path = assign(path, this.getEmptyPathProps(path));
    return this.renderPath(path);
  }

  render() {
    let children;
    if (isEmpty(this.pathData.contents)) {
      children = this.renderPathWithoutContents();
    } else {
      children = this.renderPathContents();
    }
    return createElement(this.groupComponent, {
      key: this.pathData.id,
      grouped: children.length > 1,
      children,
    });
  }
}
